import React, { useState, useEffect } from 'react'
import { Input, Button, Card, Space, Typography, Row, Col, Checkbox } from 'antd'
import { ContainerFull } from '../components/GeneralComponents'
import styled from 'styled-components'

const { TextArea } = Input
const { Title } = Typography

// Styled components for the 96-well plate
const PlateContainer = styled.div`
  display: grid;
  grid-template-columns: auto repeat(12, 1fr);
  grid-template-rows: auto repeat(8, 1fr);
  gap: 4px;
  max-width: 1200px;
  margin: 20px auto;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
`

const WellLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  color: #666;
`

const Well = styled.div<{ $hasContent: boolean }>`
  width: 70px;
  height: 60px;
  border: 2px solid ${props => props.$hasContent ? '#1890ff' : '#ddd'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$hasContent ? '#e6f7ff' : '#fff'};
  font-size: 12px;
  font-weight: ${props => props.$hasContent ? 'bold' : 'normal'};
  color: ${props => props.$hasContent ? '#1890ff' : '#999'};
  text-align: center;
  word-break: break-all;
  overflow: hidden;
  cursor: ${props => props.$hasContent ? 'pointer' : 'default'};
  transition: all 0.3s;
`

interface PlateData {
  [position: string]: string // position -> sampleId
}

interface DisplayOptions {
  sampleId: boolean
  wellIndex: boolean
  wellPosition: boolean
}

export const Tools: React.FC = () => {
  const [inputData, setInputData] = useState('')
  const [plateData, setPlateData] = useState<PlateData>({})
  const [error, setError] = useState('')
  const [reads, setReads] = useState('')
  const [minutes, setMinutes] = useState('')
  const [sampleCount, setSampleCount] = useState('')
  const [displayOptions, setDisplayOptions] = useState<DisplayOptions>({
    sampleId: true,
    wellIndex: false,
    wellPosition: false
  })

  const readsValue = Number(reads)
  const minutesValue = Number(minutes)
  const sampleCountValue = Number(sampleCount)
  const kReadsPerMinute = readsValue > 0 && minutesValue > 4 ? (readsValue / 1000) / (minutesValue - 4) : 0
  const recommendedMinutes = kReadsPerMinute > 0 && sampleCountValue > 0 ? (sampleCountValue * 1) / kReadsPerMinute : 0

  // Generate position labels (A1-H12)
  
  const parseInputData = () => {
    setError('')
    const newPlateData: PlateData = {}
    
    if (!inputData.trim()) {
      setPlateData({})
      return
    }

    const lines = inputData.trim().split('\n')
    let startLine = 0
    
    // Check if first line is a header (contains non-position data in second column)
    if (lines.length > 0) {
      const firstLine = lines[0].trim()
      if (firstLine) {
        const parts = firstLine.split('\t')
        if (parts.length >= 2) {
          const potentialPosition = parts[1].trim().toUpperCase()
          const positionRegex = /^[A-H](?:1[0-2]|[1-9])$/
          
          // If second column doesn't look like a position, assume it's a header
          if (!positionRegex.test(potentialPosition)) {
            startLine = 1 // Skip the header line
          }
        }
      }
    }
    
    for (let i = startLine; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      
      // Split by tab or multiple spaces, but preserve empty fields
      const parts = line.split('\t')
      
      // Handle different input formats
      let sampleId = ''
      let position = ''
      
      if (parts.length >= 2) {
        // Tab-separated: handle empty sample ID
        sampleId = parts[0] ? parts[0].trim() : ''
        position = parts[1].trim()
      } else {
        // Try space-separated as fallback
        const spaceParts = line.split(/\s{2,}/).filter(part => part.trim())
        if (spaceParts.length >= 2) {
          sampleId = spaceParts[0].trim()
          position = spaceParts[1].trim()
        } else if (spaceParts.length === 1) {
          // Only position provided (empty sample ID case)
          sampleId = ''
          position = spaceParts[0].trim()
        } else {
          setError(`Line ${i + 1}: Expected at least position data`)
          return
        }
      }
      
      const normalizedPosition = position.toUpperCase().trim()
      
      // Validate position format (A1-H12)
      const positionRegex = /^[A-H](?:1[0-2]|[1-9])$/
      if (!positionRegex.test(normalizedPosition)) {
        setError(`Line ${i + 1}: Invalid position "${position}". Expected format: A1-H12`)
        return
      }
      
      // Store position even if sample ID is empty
      newPlateData[normalizedPosition] = sampleId
    }
    
    setPlateData(newPlateData)
  }

  useEffect(() => {
    parseInputData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputData])

  const clearData = () => {
    setInputData('')
    setPlateData({})
    setError('')
  }

  const generateSampleData = () => {
    const sampleData = [
      'Sample001\tA1',
      'Sample002\tA2',
      'Sample003\tA3',
      '\tA4',
      'Control_Pos\tH11',
      'Control_Neg\tH12'
    ].join('\n')
    setInputData(sampleData)
  }

  const renderPlate = () => {
    const rows = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
    const cols = Array.from({ length: 12 }, (_, i) => i + 1)

    const getWellContent = (row: string, col: number, position: string) => {
      const hasPosition = plateData.hasOwnProperty(position)
      const sampleId = plateData[position]
      const hasContent = hasPosition && !!sampleId
      const isEmpty = hasPosition && !sampleId
      const wellIndex = rows.indexOf(row) * 12 + col

      const displayParts: string[] = []

      if (displayOptions.sampleId) {
        if (hasContent) {
          displayParts.push(sampleId)
        } else if (!isEmpty) {
          // Only show position if no sample data and we're not showing wellPosition separately
          if (!displayOptions.wellPosition) {
            displayParts.push(position)
          }
        }
      }

      if (displayOptions.wellIndex) {
        displayParts.push(`#${wellIndex}`)
      }

      if (displayOptions.wellPosition) {
        displayParts.push(position)
      }

      // If nothing is selected or no content to show, show position as fallback
      if (displayParts.length === 0) {
        return position
      }

      return displayParts.join('\n')
    }

    return (
      <>
        
        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
          <Space size="large">
            <Checkbox 
              checked={displayOptions.sampleId} 
              onChange={(e) => setDisplayOptions(prev => ({ ...prev, sampleId: e.target.checked }))}
            >
              Sample ID
            </Checkbox>
            <Checkbox 
              checked={displayOptions.wellIndex} 
              onChange={(e) => setDisplayOptions(prev => ({ ...prev, wellIndex: e.target.checked }))}
            >
              Well Index (1-96)
            </Checkbox>
            <Checkbox 
              checked={displayOptions.wellPosition} 
              onChange={(e) => setDisplayOptions(prev => ({ ...prev, wellPosition: e.target.checked }))}
            >
              Well Position (A1-H12)
            </Checkbox>
          </Space>
        </div>
        <PlateContainer>
          {/* Empty top-left cell */}
          <WellLabel></WellLabel>
          
          {/* Column headers (1-12) */}
          {cols.map(col => (
            <WellLabel key={`col-${col}`}>{col}</WellLabel>
          ))}
          
          {/* Rows with row headers and wells */}
          {rows.map(row => (
            <React.Fragment key={row}>
              <WellLabel>{row}</WellLabel>
              {cols.map(col => {
                const position = `${row}${col}`
                const hasPosition = plateData.hasOwnProperty(position)
                const sampleId = plateData[position]
                const hasContent = hasPosition && !!sampleId
                
                return (
                  <Well 
                    key={position} 
                    $hasContent={hasContent}
                    title={hasPosition ? (sampleId ? `${position}: ${sampleId}` : `${position}: Empty`) : position}
                  >
                    {getWellContent(row, col, position)}
                  </Well>
                )
              })}
            </React.Fragment>
          ))}
        </PlateContainer>
      </>
    )
  }

  return (
    <ContainerFull style={{ fontFamily: '"Rubik", sans-serif', padding: '24px' }}>
      <Title level={2}>Admin Tools</Title>

      <Title level={3}>Run time calculator</Title>
      <Card size="small" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <span>Reads</span>
              <Input
                type="number"
                min={0}
                value={reads}
                onChange={(e) => setReads(e.target.value)}
                placeholder="Enter total reads"
              />
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <span>Minutes</span>
              <Input
                type="number"
                min={0}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="Enter run minutes"
              />
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <span># of samples</span>
              <Input
                type="number"
                min={0}
                value={sampleCount}
                onChange={(e) => setSampleCount(e.target.value)}
                placeholder="Enter number of samples"
              />
            </Space>
          </Col>
        </Row>

        <Space direction="vertical" style={{ marginTop: 16 }}>
          <div>
            <strong>k reads per minute:</strong> {kReadsPerMinute.toFixed(2)}
          </div>
          <div>
            <strong>Recommended minutes of run:</strong> {recommendedMinutes.toFixed(2)}
          </div>
          <div>
            <strong>Recommended hours of run:</strong> {(recommendedMinutes / 60).toFixed(2)}
          </div>
        </Space>
      </Card>
      

      <Title level={3}>96-Well Plate Layout Generator</Title>
      
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Sample Data Input" size="small">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <p style={{ marginBottom: 8, color: '#666' }}>
                  Paste your data with Sample ID and Position columns (tab or space separated):
                </p>
                <TextArea
                  value={inputData}
                  onChange={(e) => setInputData(e.target.value)}
                  placeholder={`Sample001\tA1\nSample002\tA2\n\tA3\nControl_Pos\tH11\nControl_Neg\tH12`}
                  rows={8}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              
              <Space>
                <Button onClick={generateSampleData} type="default">
                  Load Sample Data
                </Button>
                <Button onClick={clearData} type="default">
                  Clear All
                </Button>
              </Space>
              
              {error && (
                <div style={{ color: '#ff4d4f', padding: '8px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              
              <div style={{ fontSize: '12px', color: '#999' }}>
                <strong>Format:</strong> Each line should contain Sample ID and Position (A1-H12)<br/>
                <strong>Example:</strong> Sample001&nbsp;&nbsp;&nbsp;&nbsp;A1
              </div>
            </Space>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="Plate Statistics" size="small">
            <Space direction="vertical">
              <div><strong>Total Wells:</strong> 96</div>
              <div><strong>Filled Wells:</strong> {Object.keys(plateData).length}</div>
              <div><strong>Empty Wells:</strong> {96 - Object.keys(plateData).length}</div>
              <div><strong>Fill Percentage:</strong> {((Object.keys(plateData).length / 96) * 100).toFixed(1)}%</div>
            </Space>
          </Card>
        </Col>
      </Row>
      
      <Card title={"96-Well Plate Layout: #"+Object.values(plateData).filter(sampleId => !!sampleId).length +" samples"} style={{ marginTop: 24 }} size="small">
        
        {renderPlate()}
      </Card>
    </ContainerFull>
  )
}

export default Tools